import React, {useCallback, useId, useState} from "react";
import {useMessages} from "i18n";
import {
	FOCAL_POINT_CENTRE,
	focalPointAt,
	focalPointPercent,
	isCentredFocalPoint,
	isFocalPoint,
	nudgeFocalPoint,
	type FocalPoint,
} from "./mediaFit";
import {
	FocalMarker,
	FocalMedia,
	FocalReadout,
	FocalStage,
	OptionError,
	OptionField,
	OptionHint,
	OptionLegend,
} from "./optionControls.styles";
import {FOCAL_MARKER_TESTID, FOCAL_STAGE_TESTID} from "../takeover/takeoverTestIds";

/**
 * Where the advertiser's one wide file is cropped around (kitecorp/transferify-ui#2143, epic
 * kitecorp/transferify#255).
 *
 * A wallpaper booking buys ONE picture or clip, and every recipient's window keeps a different part of
 * it: a phone holds roughly a third of a landscape frame's width. The owner's decision of 2026-09-06 is
 * a wide master cropped around a point rather than a second portrait cut, so this control is where that
 * point is chosen - one marker, dragged over the media, defaulting to the centre.
 *
 * Dumb by mandate (epic #26): every rule it needs is in `mediaFit.ts`, which imports no React and is
 * tested against literals. This component converts a pointer into a fraction of one element's box and
 * hands it up; it clamps nothing, rounds nothing and decides nothing.
 *
 * ## The keyboard is not a fallback here
 *
 * A two-axis picker has no native control and no ARIA role that fits, so the marker is a real
 * `<button>` that the arrow keys move - `focus`, `Enter`-free activation and the announcement all come
 * from the element rather than from a hand-rolled widget. The chosen point is also published as WORDS
 * in a live region under the picture: without it, a reader who cannot see the marker moves it and hears
 * nothing at all. Those words are the same phrase the review step and the operator queue use, so what
 * the advertiser hears while dragging is what they confirm before paying.
 */

export interface FocalPointCopy {
	label: string;
	hint: string;
	/** The marker's own accessible name; it must say that the arrow keys move it. */
	markerAria: string;
	/** Shown in the picture's place before anything has been uploaded. */
	empty: string;
}

export interface FocalPointFieldProps {
	copy: FocalPointCopy;
	/**
	 * The picked media, or `null` before there is any.
	 *
	 * A STILL, always: a video treatment passes its poster frame, because a marker dragged over a
	 * playing clip is aimed at whichever frame happened to be on screen. The poster is the frame the
	 * advertiser chose to stand for the video, and it is what a reduced-motion recipient sees anyway.
	 */
	mediaUrl: string | null;
	/** The stored point, or absent for the centre. */
	value: FocalPoint | undefined;
	onChange: (point: FocalPoint) => void;
	error: string | null;
}

export function FocalPointField({
	copy, mediaUrl, value, onChange, error,
}: FocalPointFieldProps): React.ReactElement {
	const m = useMessages().sponsor.options.focalPoint;
	const id = useId();
	const hintId = `${id}-hint`;
	const errorId = `${id}-error`;
	const readoutId = `${id}-readout`;
	/*
	 * The stage element, held as STATE rather than as a ref: a pointer handler needs its rect, and the
	 * element only exists after the first commit. The same arrangement `WallpaperStage` and
	 * `PosterCanvas` use, and for the same reason.
	 */
	const [stage, setStage] = useState<HTMLDivElement | null>(null);
	/*
	 * The marker element, held for one reason: a POINTER placement has to leave the keyboard on it.
	 * Without this the arrow keys after a tap reach the document and scroll the page instead of moving
	 * the point the person just placed (kitecorp/transferify-ui#2143's review).
	 */
	const [marker, setMarker] = useState<HTMLButtonElement | null>(null);
	// The point as PAINTED, which is what an unusable stored value resolves to as well.
	const point = isFocalPoint(value) ? value : FOCAL_POINT_CENTRE;

	const moveTo = useCallback((event: React.PointerEvent) => {
		if (stage === null) {
			return;
		}
		const box = stage.getBoundingClientRect();
		// A zero-width box is a stage that has not been laid out yet; dividing by it would hand
		// `focalPointAt` an infinity, which it answers with the centre - a silent jump under the hand.
		if (box.width === 0 || box.height === 0) {
			return;
		}
		onChange(focalPointAt((event.clientX - box.left) / box.width, (event.clientY - box.top) / box.height));
	}, [stage, onChange]);

	const onArrow = (event: React.KeyboardEvent) => {
		const step = ARROWS[event.key];
		if (step === undefined) {
			return;
		}
		// The page scrolls on the arrow keys otherwise, so a reader moving the marker would also be
		// moving the form out from under it.
		event.preventDefault();
		onChange(nudgeFocalPoint(point, step.dx, step.dy));
	};

	const spoken = isCentredFocalPoint(point)
		? m.centre
		: readPoint(point, m.at);

	return (
		<OptionField>
			<OptionLegend>{copy.label}</OptionLegend>
			{mediaUrl === null
				? <OptionHint>{copy.empty}</OptionHint>
				: (
					<FocalStage
						ref={setStage}
						data-testid={FOCAL_STAGE_TESTID}
						/*
						 * The pointer is CAPTURED on the stage, so a drag that leaves the picture keeps
						 * arriving here and the marker follows to the edge it is clamped at. Without it a
						 * gesture that overshoots simply stops, leaving the point wherever the pointer
						 * happened to cross the boundary.
						 */
						onPointerDown={event => {
							event.currentTarget.setPointerCapture(event.pointerId);
							moveTo(event);
							// The keyboard follows the hand: a placement that left focus on the page would
							// make the next arrow press scroll instead of nudge.
							marker?.focus();
						}}
						onPointerMove={event => {
							if (event.currentTarget.hasPointerCapture(event.pointerId)) {
								moveTo(event);
							}
						}}
					>
						{/*
						  * DECORATIVE, exactly as the picker's own tiles are: the advertiser is looking at
						  * their own upload, and everything a reader needs from this control is on the
						  * marker's label and in the live readout below. An alt describing the picture
						  * would announce it before every one of those.
						  */}
						<FocalMedia
							src={mediaUrl}
							alt=""
							/*
							 * Dragged rather than dropped: a browser's own image drag would start a
							 * file drag and cancel the pointer capture mid-gesture.
							 */
							draggable={false}
						/>
						<FocalMarker
							ref={setMarker}
							type="button"
							data-testid={FOCAL_MARKER_TESTID}
							/*
							 * The POSITION is in the name, not only in the live region beside it: a reader
							 * who tabs onto this control hears where the point already is rather than
							 * having to move it to find out. The live readout still announces each change.
							 */
							aria-label={`${copy.markerAria} ${spoken}`}
							aria-describedby={error === null
								? `${readoutId} ${hintId}`
								: `${readoutId} ${hintId} ${errorId}`}
							style={{left: `${point.x * 100}%`, top: `${point.y * 100}%`}}
							onKeyDown={onArrow}
						/>
					</FocalStage>
				)}
			{/*
			  * The point in words, and a LIVE region: a reader who cannot see the marker gets nothing at
			  * all from moving it otherwise. Polite rather than assertive, because a held arrow key
			  * would otherwise interrupt itself on every repeat.
			  */}
			<FocalReadout id={readoutId} aria-live="polite">{spoken}</FocalReadout>
			<OptionHint id={hintId}>{copy.hint}</OptionHint>
			{error !== null && <OptionError id={errorId} role="alert">{error}</OptionError>}
		</OptionField>
	);
}

/** Which way each arrow moves the marker. A table, so the handler has no four-branch condition. */
const ARROWS: Record<string, {dx: number; dy: number} | undefined> = {
	ArrowLeft: {dx: -1, dy: 0},
	ArrowRight: {dx: 1, dy: 0},
	ArrowUp: {dx: 0, dy: -1},
	ArrowDown: {dx: 0, dy: 1},
};

/** The point as the catalog's phrase, in the percentages the review row and the queue also say. */
function readPoint(point: FocalPoint, at: (x: number, y: number) => string): string {
	const {x, y} = focalPointPercent(point);
	return at(x, y);
}
